import React from 'react';

import { SignatureInputFontName } from './types/SignatureInput';

const draw = ({
  canvas,
  text,
  fontName,
}: {
  canvas: HTMLCanvasElement;
  text: string;
  fontName: SignatureInputFontName;
}) => {
  const context = canvas.getContext('2d');
  if (context) {
    const width = canvas.width;
    const height = canvas.height;
    context.clearRect(0, 0, width, height);
    context.font = `32px "${fontName}", cursive`;
    context.fillStyle = 'black';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, width / 2, height / 2, width);
  }
};

type SignatureInputTypeCanvasProps = {
  nameAsSignature: string;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  fontName?: SignatureInputFontName;
};

export function SignatureInputTypeCanvas({
  nameAsSignature,
  canvasRef,
  fontName = 'Alex Brush',
}: Readonly<SignatureInputTypeCanvasProps>) {
  const canvasContainerRef = React.useRef<HTMLDivElement>(null);

  const resizeCanvas = React.useCallback(() => {
    if (canvasContainerRef.current && canvasRef.current) {
      // Resizes the canvas to fit the container
      canvasRef.current.width = canvasContainerRef.current.offsetWidth;
      canvasRef.current.height = 40;
      draw({ canvas: canvasRef?.current, text: nameAsSignature, fontName });
    }
  }, [canvasRef, nameAsSignature, fontName]);

  /**
   * Resizes the canvas to fit the container.
   */
  React.useEffect(() => {
    if (canvasContainerRef.current && canvasRef.current) {
      resizeCanvas();
    }
  }, [canvasRef, resizeCanvas, nameAsSignature]);

  /**
   * Resizes the canvas when the window is resized to fit it correctly in the container.
   */
  React.useEffect(() => {
    window.addEventListener('resize', resizeCanvas);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [resizeCanvas]);

  return (
    <div ref={canvasContainerRef} className="color-scheme-only-light bg-surface-l0">
      <canvas ref={canvasRef} />
    </div>
  );
}
